import { useEffect, useState } from 'react';
import { set } from 'unchanged';

import routes from 'js/core_pre_webpack/routes';
import { getCsrfToken } from '../util';
import { Author } from './types';

type UseAuthorsInterface = {
  authors: Author[];
  followAuthor: (author: Author, source?: string) => void;
  setAuthors: (authors: Author[]) => void;
};

const csrfToken = getCsrfToken();

export const useAuthors = (
  initialAuthors: Author[] = [],
): UseAuthorsInterface => {
  const [authors, setAuthors] = useState(initialAuthors);

  const followAuthor = async (author: Author, source?: string) => {
    const route = routes.api_v0_user_following_index_path;
    // Route stays the same, but to follow we POST,
    // to unfollow, we DELETE
    const method = author.following ? 'DELETE' : 'POST';

    const res = await fetch(
      route({ subdomain_param: 'api', user_id: author.id }),
      {
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': csrfToken,
          'Content-Type': 'application/json',
        },
        method,
        body: JSON.stringify({ source }),
      },
    );
    if (res.ok) {
      window.dispatchEvent(
        new CustomEvent('swp:follow', {
          detail: { id: author.id, following: !author.following },
        }),
      );
    }
  };

  // Sync state with other React component trees in the SWP
  useEffect(() => {
    const handler = (event: CustomEvent) => {
      const following = event.detail.following;
      const authorIndex = authors.findIndex(
        (author) => author.id === event.detail.id,
      );
      if (authorIndex !== -1) {
        setAuthors(
          set(
            [authorIndex, 'following'],
            following,
            authors,
          ) as unknown as Author[],
        );
      }
    };

    window.addEventListener('swp:follow', handler as EventListener);
    return () =>
      window.removeEventListener('swp:follow', handler as EventListener);
  }, [authors]);

  return { authors, followAuthor, setAuthors };
};
